import { useEffect, useMemo, useRef } from 'react'
import axios from 'axios'
import { logger } from '../../services/logger'
import { analytics, EVENT_NAMES } from '../../services/Analytics/Analytics'
import { getPromiseDateProps } from '../../services/Analytics/orderAnalytics'
import {
  DELIVERY_PROMISE_FETCH_SOURCE,
  getDeliveryPromiseFetchProps,
} from '../../services/Analytics/productArrivalsAnalytics'
import {
  shippingOptionsManager,
  type DeliveryPromiseAddress,
} from '../../services/ShippingOptionsManager'
import { type ShippingOptionName } from '../../stores/shippingStore/shipping.types'
import { type GroupPricingResult } from '../../features/cart/groupCheckoutPricing.types'
import { type CheckoutShippingOption } from '../../features/productArrivals/cartShippingOptions'
import { useCartDeliveryPromisesQuery } from '../../features/productArrivals/hooks/useCartDeliveryPromisesQuery'
import { useDeliveryPromisesQueryInputs } from '../../features/productArrivals/hooks/useDeliveryPromisesQueryInputs'

interface ShippingPrice {
  key: string
  currency: string
  price: number
  priceBefore: number
}

interface UseShippingOptionsParams {
  isCartCheckout: boolean
  isCheckoutOpen: boolean
  address: DeliveryPromiseAddress | null
  groupPricing: GroupPricingResult | undefined
  selectedShippingOptionName: ShippingOptionName
}

const NO_SHIPPING_OPTIONS: CheckoutShippingOption[] = []

function getDeliveryPromiseWithPricing(
  deliveryPromise: CheckoutShippingOption,
  shippingPrices: ShippingPrice[]
): CheckoutShippingOption {
  const shippingPrice = shippingPrices.find(
    (shippingPrice) => shippingPrice.key === deliveryPromise.shippingOptionName
  )
  return {
    ...deliveryPromise,
    pricing: {
      currencyCode: shippingPrice?.currency,
      cost: shippingPrice?.price || 0,
      costBefore: shippingPrice?.priceBefore || 0,
    },
  }
}

function isTimeoutError(error: Error): boolean {
  return axios.isAxiosError(error) && error.code === 'ECONNABORTED'
}

export default function useShippingOptions({
  isCartCheckout,
  isCheckoutOpen,
  address,
  groupPricing,
  selectedShippingOptionName,
}: UseShippingOptionsParams) {
  const deliveryPromisesQueryInputs = useDeliveryPromisesQueryInputs()
  const deliveryPromisesQuery = useCartDeliveryPromisesQuery({
    deliveryPromisesQueryInputs: { ...deliveryPromisesQueryInputs, address },
    enabled: isCartCheckout && isCheckoutOpen && !!address,
  })
  const { data: fetchedDeliveryPromises, error: deliveryPromisesError } =
    deliveryPromisesQuery

  // A changed bag or address loads with no data, and a failed request has none, so the methods
  // stay from the last success; only the dates wait.
  const lastDeliveryPromisesRef = useRef(NO_SHIPPING_OPTIONS)
  if (fetchedDeliveryPromises) {
    lastDeliveryPromisesRef.current = fetchedDeliveryPromises
  }
  // Only cart orders are sent with a delivery promise; other checkouts get no shipping options.
  const deliveryPromises = isCartCheckout
    ? lastDeliveryPromisesRef.current
    : NO_SHIPPING_OPTIONS
  const isCalculatingShippingOptions = deliveryPromisesQuery.isFetching
  // A background refetch keeps the previous dates as data, so they count only once it's done.
  const hasUpToDateShippingOptions =
    deliveryPromisesQuery.isSuccess && !isCalculatingShippingOptions

  // Priced by the bag's group totals, like the summary of the selected method.
  const shippingPrices = useMemo(
    (): ShippingPrice[] =>
      groupPricing
        ? Object.entries(groupPricing.shippingOptions).map(([key, option]) => ({
            key,
            currency: groupPricing.currency,
            price: option.shippingCost,
            priceBefore: option.shippingCostBeforeDiscount,
          }))
        : [],
    [groupPricing]
  )
  // A pricing error returns no prices, so the methods keep the last ones.
  const lastShippingPricesRef = useRef(shippingPrices)
  if (shippingPrices.length > 0) {
    lastShippingPricesRef.current = shippingPrices
  }
  const lastShippingPrices = lastShippingPricesRef.current

  const shippingOptions = useMemo(
    () =>
      lastShippingPrices.length > 0
        ? deliveryPromises.map((deliveryPromise) =>
            getDeliveryPromiseWithPricing(deliveryPromise, lastShippingPrices)
          )
        : NO_SHIPPING_OPTIONS,
    [deliveryPromises, lastShippingPrices]
  )
  const selectedShippingOption = shippingOptions.find(
    (option) => option.shippingOptionName === selectedShippingOptionName
  )

  // The bag drawer runs this query too, so its dates can arrive while checkout is closed or shows
  // another product. They are recorded and reported once, when a cart checkout first shows them.
  const lastShownDeliveryPromisesRef = useRef<CheckoutShippingOption[] | null>(
    null
  )
  useEffect(() => {
    if (
      !isCartCheckout ||
      !isCheckoutOpen ||
      !fetchedDeliveryPromises ||
      fetchedDeliveryPromises === lastShownDeliveryPromisesRef.current
    ) {
      return
    }
    lastShownDeliveryPromisesRef.current = fetchedDeliveryPromises
    shippingOptionsManager.recordShownShippingOptions({
      shippingOptions: fetchedDeliveryPromises,
      persist: true,
    })
    if (fetchedDeliveryPromises.length > 0) {
      analytics.track(EVENT_NAMES.DELIVERY_PROMISE_UPDATED, {
        ...getPromiseDateProps(),
        ...getDeliveryPromiseFetchProps({
          source: DELIVERY_PROMISE_FETCH_SOURCE.CHECKOUT,
          // Checkout asks for dates only with a saved address.
          hadFullAddress: true,
          state: address?.state,
        }),
      })
    }
  }, [address, fetchedDeliveryPromises, isCartCheckout, isCheckoutOpen])

  // Logged once a request has failed all its attempts. Delivery date logs get their own Sentry
  // issues (logged errors are otherwise grouped together), so an alert can count each case alone.
  // A failure that arrives while no cart checkout shows the dates is the bag drawer's, so it isn't
  // logged, also not later when checkout opens on it.
  const lastHandledDeliveryPromisesErrorRef = useRef<Error | null>(null)
  useEffect(() => {
    if (
      !deliveryPromisesError ||
      deliveryPromisesError === lastHandledDeliveryPromisesErrorRef.current
    ) {
      return
    }
    lastHandledDeliveryPromisesErrorRef.current = deliveryPromisesError
    if (!isCartCheckout || !isCheckoutOpen) {
      return
    }
    if (isTimeoutError(deliveryPromisesError)) {
      logger.error(
        'Delivery dates did not load in time',
        deliveryPromisesError,
        {
          fingerprint: ['delivery-dates', 'calculating-timeout'],
          level: 'warning',
        }
      )
    } else {
      logger.error('Error fetching delivery promise', deliveryPromisesError, {
        fingerprint: ['delivery-dates', 'request-failed'],
      })
    }
  }, [deliveryPromisesError, isCartCheckout, isCheckoutOpen])

  return {
    selectedShippingOption,
    shippingOptions,
    hasUpToDateShippingOptions,
    isCalculatingShippingOptions,
  }
}
